import { BaseApplication, CameraManager, Configuration, Graphics, NormalRender, PerspectiveCamera, ResourceContainer, Screen } from 'ohzi-core';

import CameraStreamWindow from '/js/components/html_components/camera_stream/CameraStreamWindow';
import { LoginView } from '/js/components/html_components/LoginView';
import MenuOptionsView from '/js/components/html_components/MenuOptionsView';
import MenuView from '/js/components/html_components/MenuView';
import PharosView from '/js/components/html_components/side_menus/PharosView';
import UavView from '/js/components/html_components/side_menus/UavView';

// This is the starting point of the application
/**
 * @class MainApplication
 * @description Here is the Main class that is called
 * @function __init_camera() - Initialises the perspective camera of threeJs and the pass it at the camera management
 * @function __init_camera_controller() - Initialises the camera_controller and sets the perspective camera as its main camera.
 * @function on_resize() - On resize event calls the on_resize function on PlayerContainer.(called by api.js)
 * @function on_orientation_change() - On orientationchange evenet calls on_orientation_change on PlayerContainer. (called  by api.jss)
 * @function update() - This function is called one time per frame. This function is called from RenderLoop class of ohzi-core.
 * camera_controllers is the camera that is responisble for moving the view of the video when it plays.
 */
export default class MainApplication extends BaseApplication
{
  constructor(renderer)
  {
    super();
    this.renderer = renderer;

    this.normal_render_mode = new NormalRender();

    this.loader_view = document.querySelector('.loader');

    // Set this to true to use local file storage instead of recordings server
    this.local_file_storage = false;
    this.flip_orientation = false;
    this.start_ms = new Date().getTime();

    this.last_ms = 0;
    this.debug_render_fps = 0;
    this.debug_render_fps_avg = 0;
  }

  // This function is called only one time
  // at the beginning of the execution (see main.js)
  start()
  {
    Graphics.set_state(this.normal_render_mode);
    this.__init_camera();

    this.login_view                       = new LoginView(this);
    this.menu_view                         = new MenuView(this);
    this.menu_options_view                 = new MenuOptionsView(this);
    this.uav_view                         = new UavView(this);
    this.pharos_view                      = new PharosView(this);
    this.camera_stream_window             = new CameraStreamWindow();

    this.config = ResourceContainer.get_resource('config');

    this.viewer = new Cesium.Viewer('cesiumContainer', {
      shouldAnimate: true,
      requestRenderMode: true
    });

    this.base_picker_select_imagery_layer('ESRI World Imagery');

    this.login_view.start();
    this.menu_view.start();
    this.uav_view.start();
    this.pharos_view.start();

    this.menu_options_view.start();
    this.camera_stream_window.start();

    this.configure_app();

    // Force resize update before the first frame
    // To be sure that the canvas has the correct size
    this.on_resize();
  }

  // This function is called one time per frame.
  // In an optimal execution, it is called 60 times per second
  // This function is called from RenderLoop class of ohzi-core
  update()
  {
    const now_ms = new Date().getTime();

    // Calculate main loop update rate
    const elapsed_time_ms = now_ms - this.last_ms;
    this.debug_render_fps = 1 / (elapsed_time_ms / 1000);
    this.debug_render_fps_avg = (this.debug_render_fps_avg * 0.98) + (this.debug_render_fps * 0.02);
    this.last_ms = now_ms;

    this.login_view.update();
    this.menu_view.update();
    this.uav_view.update();
    this.pharos_view.update();
    this.camera_stream_window.update();
  }

  base_picker_select_imagery_layer(name)
  {
    const index = this.viewer.baseLayerPicker.viewModel.imageryProviderViewModels.findIndex(function(layer)
    {
      // console.log(`[base_picker_select_imagery_layer] name: ${layer.name}`);
      return layer.name == name;
    });
    console.log(`Selecting terrain layer '${name}' at index ${index}`);
    if (index > -1)
    {
      this.viewer.baseLayerPicker.viewModel.selectedImagery = this.viewer.baseLayerPicker.viewModel.imageryProviderViewModels[index];
    }
  }

  // Called by api.js when the browser window is resized
  on_resize()
  {

  }

  // Called by api.js when the device orientation is changed
  on_orientation_change()
  {
  }

  configure_app()
  {
    if (Configuration.is_ios)
    {
      ViewApi.hide_recordings_tab();
      ViewApi.hide_events_tab();
      // ViewApi.hide_archive_tab();
    }
  }

  __init_camera()
  {
    const camera = new PerspectiveCamera(60, Screen.aspect_ratio, 0.1, 200);
    camera.updateProjectionMatrix();
    camera.position.z = 10;

    camera.clear_color.copy(new THREE.Color('#000000'));
    camera.clear_alpha = 1;
    CameraManager.current = camera;
  }
}
